/**
 * Photos and files on a text, an email or a note — the camera and the
 * paperclip under the message (Board 11 B, D, G), and the chips under it
 * (*Q-2207 Front walkway.pdf* ×, *2 photos* ×). An email can also take the
 * first job's own files (**From the job**, §9.4).
 *
 * Each file goes up as it is picked (`upload`, the product's one way in) and
 * only its id rides on the message; the server checks the kinds and sizes a
 * text or an email may carry.
 */
import { useEffect, useRef, useState } from 'react'
import { upload } from '../../lib/api.js'
import { Popover } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { activityRequest } from '../api.js'
import { PICKER_LAYER, ToolChip, ToolSquare } from './kit.js'

export interface AttachedFile { id: string; name: string; photo: boolean }

export function useFiles(): {
  files: AttachedFile[]
  add: (list: FileList | null) => Promise<void>
  addExisting: (more: AttachedFile[]) => void
  remove: (match: (file: AttachedFile) => boolean) => void
  busy: boolean
  problem: string | null
} {
  const [files, setFiles] = useState<AttachedFile[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const add = async (list: FileList | null) => {
    if (!list || list.length === 0) return
    setBusy(true)
    setProblem(null)
    try {
      for (const file of [...list]) {
        const photo = file.type.startsWith('image/')
        const made = await upload(file, { kind: photo ? 'photo' : 'other' })
        setFiles((was) => [...was, { id: made.id, name: file.name, photo }])
      }
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }
  return {
    files,
    add,
    addExisting: (more) => setFiles((was) => [...was, ...more.filter((one) => !was.some((had) => had.id === one.id))]),
    remove: (match) => setFiles((was) => was.filter((one) => !match(one))),
    busy,
    problem,
  }
}

/** The camera and the paperclip — hidden inputs behind two 28 px squares. */
export function FileTools({ onFiles }: { onFiles: (list: FileList | null) => void }) {
  const camera = useRef<HTMLInputElement>(null)
  const clip = useRef<HTMLInputElement>(null)
  return (
    <>
      <ToolSquare glyph="camera" label="Add a photo" onClick={() => camera.current?.click()} />
      <ToolSquare glyph="paperclip" label="Attach a file" onClick={() => clip.current?.click()} />
      <input ref={camera} type="file" accept="image/*" capture="environment" multiple hidden
        onChange={(event) => { onFiles(event.target.files); event.target.value = '' }} />
      <input ref={clip} type="file" multiple hidden
        onChange={(event) => { onFiles(event.target.files); event.target.value = '' }} />
    </>
  )
}

/** The chips under the message: each document by name, the photos as one (*2 photos*). */
export function FileChips({ files, onRemove }: { files: AttachedFile[]; onRemove: (match: (file: AttachedFile) => boolean) => void }) {
  if (files.length === 0) return null
  const photos = files.filter((file) => file.photo)
  const chip = 'inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-line pl-2.5 pr-1.5 text-[12px] font-semibold'
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {files.filter((file) => !file.photo).map((file) => (
        <span key={file.id} className={chip}>
          <span className="text-danger"><Glyph name="file" size={13} /></span>{file.name}
          <button type="button" aria-label={`Remove ${file.name}`} onClick={() => onRemove((one) => one.id === file.id)}
            className="inline-flex text-text-muted hover:text-text"><Glyph name="close" size={12} /></button>
        </span>
      ))}
      {photos.length > 0 && (
        <span className={chip}>
          <Glyph name="camera" size={13} />{photos.length === 1 ? '1 photo' : `${photos.length} photos`}
          <button type="button" aria-label="Remove the photos" onClick={() => onRemove((one) => one.photo)}
            className="inline-flex text-text-muted hover:text-text"><Glyph name="close" size={12} /></button>
        </span>
      )}
    </div>
  )
}

interface JobFile { fileId?: string; id?: string; name?: string; filename?: string; contentType?: string; content_type?: string }

/** **From the job** (§9.4): the first job's quotes and photos, ticked onto the email. */
export function FromTheJob({ jobId, onPick }: { jobId: string | null; onPick: (files: AttachedFile[]) => void }) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<AttachedFile[] | null>(null)
  const [ticked, setTicked] = useState<string[]>([])
  const anchor = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open || !jobId) return undefined
    let live = true
    activityRequest<{ items: JobFile[] }>(`/records/job/${jobId}/files`)
      .then((answer) => {
        if (!live) return
        setItems(answer.items.map((item) => {
          const id = item.fileId ?? item.id ?? ''
          const type = item.contentType ?? item.content_type ?? ''
          return { id, name: item.name ?? item.filename ?? 'File', photo: type.startsWith('image/') }
        }).filter((item) => item.id))
      })
      .catch(() => { if (live) setItems([]) })
    return () => { live = false }
  }, [open, jobId])
  if (!jobId) return null
  return (
    <span ref={anchor} className="inline-flex">
      <ToolChip glyph="layers" onClick={() => { setTicked([]); setOpen(true) }}>From the job</ToolChip>
      <Popover open={open} onClose={() => setOpen(false)} anchor={anchor.current} label="From the job" width={320}
        layer={PICKER_LAYER} maxHeight={340} className="py-1">
        <div className="flex flex-col">
          {items === null && <div className="px-3 py-2 text-[12.5px] text-text-muted">Loading…</div>}
          {items?.length === 0 && <div className="px-3 py-2 text-[12.5px] text-text-muted">The job has no files yet.</div>}
          {items?.map((item) => {
            const on = ticked.includes(item.id)
            return (
              <button key={item.id} type="button" role="checkbox" aria-checked={on}
                onClick={() => setTicked((was) => (on ? was.filter((one) => one !== item.id) : [...was, item.id]))}
                className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[13px] hover:bg-hover">
                <span className={cn('inline-flex h-4 w-4 items-center justify-center rounded border-[1.5px]',
                  on ? 'border-accent bg-accent text-white' : 'border-line-strong bg-surface')}>
                  {on && <Glyph name="check" size={11} stroke={3} />}
                </span>
                <Glyph name={item.photo ? 'camera' : 'file'} size={13} />
                <span className="truncate">{item.name}</span>
              </button>
            )
          })}
          {items && items.length > 0 && (
            <div className="flex justify-end border-t border-line-faint px-3 pb-1 pt-2">
              <button type="button" disabled={ticked.length === 0}
                onClick={() => { onPick(items.filter((item) => ticked.includes(item.id))); setOpen(false) }}
                className="inline-flex h-8 items-center rounded-lg border border-accent bg-accent px-3 text-[12.5px] font-semibold text-white disabled:opacity-50">
                Attach {ticked.length > 0 ? ticked.length : ''}
              </button>
            </div>
          )}
        </div>
      </Popover>
    </span>
  )
}
